import React from 'react'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Popconfirm,
  Spin,
  Tag,
  Tooltip,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import queryString from 'query-string'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import AppPageHeader from '../../components/AppPageHeader'
import AccessControlAction from '../../components/AccessControlAction'
import { AvatarHasName } from '../../components/Common/Avatar'
import TableComponent from '../../components/Common/TableComponent'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import { systemName } from '../../config'
import { ACL_ACTION_TYPE, ASSIGNEE_TYPE } from '../../constants'
import DashboardLayout from '../../layouts/DashboardLayout/DashboardLayout'
import { KpiV2Request } from '../../requests/KpiV2Request'
import KpiV2AddWeightModal from './KpiV2AddWeightModal'
import KpiV2DetailExpand from './KpiV2DetailExpand'
import KpiV2SubDetailExpand from './KpiV2SubDetailExpand'
import KpiV2UpsertModal from './KpiV2UpsertModal'
import {
  AddCircleOutlineIcon,
  Pen2OutlineIcon,
  TrashBin2OutlineIcon,
} from '../../components/AppIcon'
import { AppTable } from '../../components/AppTable/AppTable'

const ACTION_GROUP_STYLE = { display: 'flex', gap: 8 }

const GROUP_CARD_STYLE = {
  border: '1px solid #dbe3ef',
  borderRadius: 6,
  overflow: 'hidden',
  backgroundColor: '#dcefd2',
}

const GROUP_HEADER_BASE_STYLE = {
  padding: '8px 12px',
  lineHeight: 1.45,
  fontWeight: 500,
}

const GROUP_HEADER_GREEN_STYLE = {
  ...GROUP_HEADER_BASE_STYLE,
  background: '#dcefd2',
}
const GROUP_HEADER_BLUE_STYLE = {
  ...GROUP_HEADER_BASE_STYLE,
  background: '#d5e4fb',
}

const TAB_GROUP_STYLE = {
  display: 'flex',
  gap: 0,
  flexWrap: 'wrap',
  borderBottom: '1px solid #e8e8e8',
  width: '100%',
}

const VIEW_TABS = [
  {
    label: 'KPI Công ty',
    value: 'COMPANY',
    aclType: ACL_ACTION_TYPE.tab_cong_ty__KPI,
  },
  {
    label: 'KPI Phòng/Ban',
    value: 'DEPARTMENT',
    aclType: ACL_ACTION_TYPE.tab_phong_ban__KPI,
  },
  {
    label: 'KPI Cá nhân',
    value: 'PERSONAL',
    aclType: ACL_ACTION_TYPE.tab_ca_nhan__KPI,
  },
]

const SCORE_COLOR = score => {
  if (score == null) return '#d9d9d9'
  if (score >= 85) return '#52c41a'
  if (score >= 60) return '#faad14'
  return '#ff4d4f'
}

const KpiV2Page = props => {
  const { aclStore, authenticationStore, history, location } = props
  const queryStringParse = queryString.parse(location.search)
  const activeTab = queryStringParse.tab || null

  const isAdmin =
    authenticationStore.isAccountAdmin || authenticationStore.isSuperAdmin
  const visibleTabs = VIEW_TABS.filter(tab => {
    if (isAdmin) return true
    const entry = aclStore.aclActionsByUser.find(
      item => item.code === tab.aclType
    )
    return !!entry?.status
  })

  const effectiveTab = visibleTabs.length
    ? visibleTabs.find(t => t.value === activeTab)?.value ??
      visibleTabs[0]?.value
    : activeTab

  const handleTabChange = value => {
    setExpandedKpiId(null)
    setExpandedSubKpiId(null)
    history.replace(`${location.pathname}?tab=${value}`)
  }

  const [expandedKpiId, setExpandedKpiId] = useState(null)
  const [expandedSubKpiId, setExpandedSubKpiId] = useState(null)
  const [kpiList, setKpiList] = useState([])
  const [subKpiList, setSubKpiList] = useState([])
  const [aspects, setAspects] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingSubKpi, setLoadingSubKpi] = useState(false)
  const [filterDates, setFilterDates] = useState([null, null])
  const [showCreate, setShowCreate] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [editData, setEditData] = useState(null)
  const [showAddWeight, setShowAddWeight] = useState(false)
  const [editAspectData, setEditAspectData] = useState(null)

  const fetchAspects = useCallback(async () => {
    try {
      const res = await KpiV2Request.getAllAspects()
      setAspects(res.data || [])
    } catch {
      // silent
    }
  }, [])

  const fetchKpiList = useCallback(async () => {
    setLoading(true)
    try {
      const params = { size: 200 }
      if (filterDates[0])
        params.start_date = filterDates[0].format('YYYY-MM-DD')
      if (filterDates[1]) params.end_date = filterDates[1].format('YYYY-MM-DD')
      const res = await KpiV2Request.getAll(params)
      setKpiList(res.data?.content ?? res.data ?? [])
    } catch {
      message.error('Không thể tải danh sách KPI.')
    } finally {
      setLoading(false)
    }
  }, [filterDates])

  const fetchSubKpiList = useCallback(async () => {
    setLoadingSubKpi(true)
    try {
      const params = { size: 200 }
      if (filterDates[0])
        params.report_date_from = filterDates[0].format('YYYY-MM-DD')
      if (filterDates[1])
        params.report_date_to = filterDates[1].format('YYYY-MM-DD')
      const res = await KpiV2Request.getAllSubKpi(params)
      setSubKpiList(res.data?.content ?? res.data ?? [])
    } catch {
      message.error('Không thể tải danh sách KPI phụ.')
    } finally {
      setLoadingSubKpi(false)
    }
  }, [filterDates])

  useEffect(() => {
    fetchAspects()
  }, [fetchAspects])

  useEffect(() => {
    if (effectiveTab === 'PERSONAL') {
      fetchSubKpiList()
    } else {
      fetchKpiList()
    }
  }, [effectiveTab, fetchKpiList, fetchSubKpiList])

  const handleDeleteAspect = async id => {
    try {
      await KpiV2Request.deleteAspect(id)
      message.success('Xóa khía cạnh thành công!')
      fetchAspects()
    } catch (error) {
      message.error(error?.vi || 'Xóa khía cạnh thất bại.')
    }
  }

  const aspectMap = useMemo(() => {
    const map = {}
    aspects.forEach(a => {
      map[a.id] = a
    })
    return map
  }, [aspects])

  const groupedRows = useMemo(() => {
    const groups = {}
    aspects.forEach(asp => {
      groups[asp.id] = { aspectId: asp.id, rows: [] }
    })
    kpiList.forEach((row, idx) => {
      const key = row.aspect_id || '__no_aspect__'
      if (!groups[key]) groups[key] = { aspectId: key, rows: [] }
      groups[key].rows.push({ ...row, key: row.id || idx })
    })
    return Object.values(groups)
  }, [kpiList, aspects])

  // SubKPI also grouped by aspect for visual consistency
  const groupedSubKpiRows = useMemo(() => {
    const groups = {}
    aspects.forEach(asp => {
      groups[asp.id] = { aspectId: asp.id, rows: [] }
    })
    subKpiList.forEach((row, idx) => {
      const key = row.aspect_id || '__no_aspect__'
      if (!groups[key]) groups[key] = { aspectId: key, rows: [] }
      groups[key].rows.push({ ...row, key: row.id || idx })
    })
    return Object.values(groups)
  }, [subKpiList, aspects])

  const renderAspectDeleteBtn = id => (
    <AccessControlAction aclActionType={ACL_ACTION_TYPE.delete__ASPECT_KPI}>
      <Popconfirm
        title="Bạn có chắc chắn muốn xóa khía cạnh này không?"
        onConfirm={() => handleDeleteAspect(id)}
        okText="Xóa"
        cancelText="Hủy">
        <Button
          style={{ borderRadius: 8 }}
          size="small"
          danger
          icon={<TrashBin2OutlineIcon style={{ width: 16, height: 16 }} />}
        />
      </Popconfirm>
    </AccessControlAction>
  )

  const columns = useMemo(
    () => [
      {
        title: 'STT',
        dataIndex: '__stt',
        width: 45,
        align: 'center',
        render: (_, __, i) => i + 1,
      },
      {
        title: 'Mục tiêu',
        dataIndex: 'purpose',
        width: 160,
        ellipsis: true,
        render: v =>
          v ? (
            <Tooltip title={v} placement="topLeft">
              <span>{v}</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        title: 'KGI',
        dataIndex: 'kgi',
        width: 160,
        ellipsis: true,
        render: v =>
          v ? (
            <Tooltip title={v} placement="topLeft">
              <span>{v}</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        title: 'CSF',
        dataIndex: 'csf',
        width: 130,
        ellipsis: true,
        render: v =>
          v ? (
            <Tooltip title={v} placement="topLeft">
              <span>{v}</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        title: 'KPI',
        dataIndex: 'kpi',
        width: 130,
        ellipsis: true,
        render: v =>
          v ? (
            <Tooltip title={v} placement="topLeft">
              <span>{v}</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        title: 'Đơn vị',
        dataIndex: 'measurement',
        width: 75,
        align: 'center',
        render: v => v || '-',
      },
      {
        title: 'TS chỉ tiêu',
        dataIndex: 'indicator_weight',
        width: 85,
        align: 'center',
        render: v => (v != null ? `${v}%` : '-'),
      },
      {
        title: 'TS chung',
        dataIndex: 'overall_weight',
        width: 80,
        align: 'center',
        render: v => (v != null ? `${v}%` : '-'),
      },
      {
        title: 'Thời gian',
        dataIndex: 'start_date',
        width: 160,
        align: 'center',
        render: (_, record) => {
          const s = record.start_date
            ? moment(record.start_date).format('DD/MM/YYYY')
            : ''
          const e = record.end_date
            ? moment(record.end_date).format('DD/MM/YYYY')
            : ''
          return s && e ? `${s} - ${e}` : s || e || '-'
        },
      },
    ],
    []
  )

  // Columns specific to Sub KPI (personal tab)
  const subKpiColumns = useMemo(
    () => [
      {
        title: 'STT',
        width: 45,
        align: 'center',
        render: (_, __, i) => i + 1,
      },
      {
        title: 'Mục tiêu',
        dataIndex: 'purpose',
        width: 150,
        ellipsis: true,
        render: v =>
          v ? (
            <Tooltip title={v} placement="topLeft">
              <span>{v}</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        title: 'KPI',
        dataIndex: 'kpi',
        width: 140,
        ellipsis: true,
        render: v =>
          v ? (
            <Tooltip title={v} placement="topLeft">
              <span>{v}</span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        title: 'Đơn vị',
        dataIndex: 'measurement',
        width: 70,
        align: 'center',
        render: v => v || '-',
      },
      {
        title: 'TS chỉ tiêu',
        dataIndex: 'indicator_weight',
        width: 85,
        align: 'center',
        render: v => (v != null ? `${v}%` : '-'),
      },
      {
        title: 'Thời gian báo cáo',
        dataIndex: 'report_date',
        width: 160,
        align: 'center',
        render: (v, record) => {
          if (!v) return '-'
          const start = moment(v).format('DD/MM/YYYY')
          const end = record.report_end_date
            ? moment(record.report_end_date).format('DD/MM/YYYY')
            : null
          return end ? `${start} - ${end}` : start
        },
      },
      {
        title: 'Người thực hiện',
        width: 140,
        render: (_, record) => {
          const handler = (record.assignees || []).find(
            a => a.permission === ASSIGNEE_TYPE.HANDLER
          )
          const displayName = handler?.name_uppercase
            ? handler.name_uppercase
                .toLowerCase()
                .replace(/(^|\s)\S/g, c => c.toUpperCase())
            : null
          return displayName ? <Tag color="blue">{displayName}</Tag> : '-'
        },
      },
      {
        title: 'Người tạo',
        width: 140,
        render: (_, record) => {
          const creator = (record.assignees || []).find(
            a => a.permission === ASSIGNEE_TYPE.CREATE
          )
          return creator?.name_uppercase ? (
            <AvatarHasName
              imgId={creator.image}
              name={creator.name_uppercase}
            />
          ) : (
            '-'
          )
        },
      },
    ],
    []
  )

  const renderAspectHeader = (aspect, gIdx) => {
    if (!aspect) return null
    const headerStyle =
      gIdx % 2 === 1 ? GROUP_HEADER_BLUE_STYLE : GROUP_HEADER_GREEN_STYLE
    return (
      <div
        style={{
          ...headerStyle,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <div>
          <div>- {aspect.context}</div>
          {aspect.weight != null && <div>- Trọng số ({aspect.weight}%)</div>}
        </div>
        {!isPersonalTab && (
          <div style={{ display: 'flex', gap: 4 }}>
            <AccessControlAction
              aclActionType={ACL_ACTION_TYPE.edit__ASPECT_KPI}>
              <Tooltip title="Chỉnh sửa">
                <Button
                  style={{ borderRadius: 8 }}
                  size="small"
                  icon={<Pen2OutlineIcon style={{ width: 16, height: 16 }} />}
                  onClick={() => {
                    setEditAspectData(aspect)
                    setShowAddWeight(true)
                  }}
                />
              </Tooltip>
            </AccessControlAction>
            {renderAspectDeleteBtn(aspect.id)}
          </div>
        )}
      </div>
    )
  }

  const isPersonalTab = effectiveTab === 'PERSONAL'
  const activeLoading = isPersonalTab ? loadingSubKpi : loading
  const activeGroups = isPersonalTab ? groupedSubKpiRows : groupedRows
  const activeColumns = isPersonalTab ? subKpiColumns : columns
  const activeScroll = isPersonalTab ? { x: 1050 } : { x: 1025 }

  return (
    <DashboardLayout>
      <Helmet>
        <title>KPI | {systemName}</title>
      </Helmet>
      <AppPageHeader
        title="KPI"
        addon={
          <>
            {!isPersonalTab && (
              <AccessControlAction
                aclActionType={ACL_ACTION_TYPE.create__ASPECT_KPI}>
                <Button
                  onClick={() => {
                    setEditAspectData(null)
                    setShowAddWeight(true)
                  }}>
                  Thêm trọng số
                </Button>
              </AccessControlAction>
            )}
            {!isPersonalTab && (
              <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__KPI}>
                <Button
                  type="primary"
                  className="custom-ant-button icon-btn"
                  onClick={() => setShowCreate(true)}
                  icon={
                    <AddCircleOutlineIcon
                      style={{ color: '#fff', width: 16, height: 16 }}
                    />
                  }>
                  Tạo KPI mới
                </Button>
              </AccessControlAction>
            )}
          </>
        }
      />

      <ContentBlockWrapper style={{ minHeight: 'calc(100vh - 150px)' }}>
        <Spin spinning={activeLoading}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Tab navigation */}
            <div style={TAB_GROUP_STYLE}>
              {visibleTabs.map(tab => (
                <div
                  key={tab.value}
                  onClick={() => handleTabChange(tab.value)}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontWeight: effectiveTab === tab.value ? 600 : 400,
                    color: effectiveTab === tab.value ? '#1890ff' : '#595959',
                    borderBottom:
                      effectiveTab === tab.value
                        ? '2px solid #1890ff'
                        : '2px solid transparent',
                    marginBottom: -1,
                    transition: 'all 0.2s',
                    userSelect: 'none',
                  }}>
                  {tab.label}
                </div>
              ))}
            </div>

            {/* Filter bar — chỉ hiển thị ở tab Cá nhân */}
            {effectiveTab === 'PERSONAL' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                  Thời gian báo cáo:
                </span>
                <DatePicker.RangePicker
                  value={filterDates}
                  onChange={dates => setFilterDates(dates || [null, null])}
                  format="DD/MM/YYYY"
                  placeholder={['Từ ngày', 'Đến ngày']}
                  allowClear
                  style={{ width: 260 }}
                />
              </div>
            )}

            {/* Content: grouped table */}
            {activeGroups.map((group, gIdx) => {
              const aspect = aspectMap[group.aspectId]
              if (!aspect && group.rows.length === 0) return null

              return (
                <section key={group.aspectId} style={GROUP_CARD_STYLE}>
                  {aspect && renderAspectHeader(aspect, gIdx)}

                  <AppTable
                    rowKey="key"
                    dataSource={group.rows}
                    columns={activeColumns}
                    onRow={record => ({
                      onClick: () => {
                        if (isPersonalTab) {
                          setExpandedSubKpiId(prev =>
                            prev === record.id ? null : record.id
                          )
                        } else {
                          setExpandedKpiId(prev =>
                            prev === record.id ? null : record.id
                          )
                        }
                      },
                      style: { cursor: 'pointer', background: 'white' },
                    })}
                    expandable={{
                      expandedRowKeys: isPersonalTab
                        ? expandedSubKpiId
                          ? [expandedSubKpiId]
                          : []
                        : expandedKpiId
                        ? [expandedKpiId]
                        : [],
                      showExpandColumn: false,
                      expandIcon: () => null,
                      expandedRowRender: record =>
                        isPersonalTab ? (
                          <KpiV2SubDetailExpand
                            subKpiId={record.id}
                            aspects={aspects}
                            onDeleted={() => {
                              setExpandedSubKpiId(null)
                              fetchSubKpiList()
                            }}
                            onUpdated={fetchSubKpiList}
                          />
                        ) : (
                          <KpiV2DetailExpand
                            kpiId={record.id}
                            aspects={aspects}
                            onDeleted={() => {
                              setExpandedKpiId(null)
                              fetchKpiList()
                            }}
                            onUpdated={fetchKpiList}
                          />
                        ),
                    }}
                    pagination={false}
                    size="small"
                    scroll={activeScroll}
                    locale={{
                      emptyText: (
                        <span style={{ display: 'block', height: 6 }} />
                      ),
                    }}
                  />
                </section>
              )
            })}
          </div>
        </Spin>
      </ContentBlockWrapper>

      <KpiV2UpsertModal
        visible={showCreate}
        onClose={() => setShowCreate(false)}
        onSuccess={fetchKpiList}
        aspects={aspects}
      />

      <KpiV2UpsertModal
        visible={showEdit}
        onClose={() => {
          setShowEdit(false)
          setEditData(null)
        }}
        onSuccess={fetchKpiList}
        aspects={aspects}
        initialData={editData}
      />

      <KpiV2AddWeightModal
        visible={showAddWeight}
        onClose={() => {
          setShowAddWeight(false)
          setEditAspectData(null)
        }}
        onSuccess={fetchAspects}
        editData={editAspectData}
      />
    </DashboardLayout>
  )
}

export default inject('aclStore', 'authenticationStore')(observer(KpiV2Page))
